<template>
    <Select
        ref="select"
        :options="options"
        :placeholder="$t(`search.searchBar.input.all`)"
        class="w-full text-black global-search bg-transparent border-0"
        :no-drop="!keyword && !options.length"
        background="transparent"
        autocomplete
        searchable
        truncate
        no-indicator
        clearable
        icon="icon-search"
        v-bind="$attrs"
        editable-input
        close-on-select
        @keydown.enter="handleKeydown"
        @input="onInput"
        @search="filterRecentSearches"
        @clear="clearSearch"
        @icon-click="submitSearch(keyword)"
    >
        <template #option="{ option }">
            <div
                class="transition-all p-4 font-medium block border-0 w-full text-slate-600 leading-6"
                @click="onInput(option)"
                v-text="displayValue(option)"
            ></div>
        </template>
        <template
            v-if="hasRecentSearch"
            #options-footer
        >
            <Btn
                variant="text"
                class="text-left w-full"
                color="blue-600"
                :label="$t('search.searchBar.clearRecentSearches')"
                size="lg"
                @click="clearRecentSearches"
            />
        </template>
    </Select>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { mapGetters } from 'vuex';
import route from '@/router/route';
import type { RecentSearch } from './RecentSearch';

/**
 * Global search bar component uses Select component to render the select dropdown & enabling
 * searchable prop allows users to enter the query & also render recent search terms in case if there is any.
 */
export default defineComponent({
    name: 'SearchGlobal',
    inheritAttrs: false,
    props: {
        /**
         * Display recent searches in the search bar
         */
        enableRecentSearch: {
            type: Boolean,
            default: false,
        },
        /**
         * Optional university ID param to pass to the search query
         */
        universityId: {
            type: Number,
            default: undefined,
        },
        /**
         * enable navigation in case parent doesn't need to catch the event before navigation
         */
        withNavigation: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['input', 'clear'],
    data() {
        return {
            keyword: '' as string,
        };
    },
    computed: {
        ...mapGetters({
            recentSearches: 'search/recentSearches',
            params: 'search/searchParams',
        }),
        hasRecentSearch() {
            return (
                this.enableRecentSearch &&
                this.options.filter(
                    (option: { selectable: boolean; temporary?: boolean }) => option.selectable && !option?.temporary,
                ).length
            );
        },
        options(): RecentSearch[] {
            let options: RecentSearch[] = (this.enableRecentSearch ? this.recentSearches : [])
                .filter((search: RecentSearch) => search.phrase)
                .map((search: RecentSearch, index: number) => ({
                    ...search,
                    id: index,
                    label: search.phrase,
                    selectable: true,
                }))
                .filter(
                    (item: RecentSearch) =>
                        typeof item.phrase === 'string' && item?.phrase?.toLowerCase().includes(this.keyword),
                );

            if (this.keyword !== '') {
                options = options.sort((a: RecentSearch, b: RecentSearch) => {
                    const aPhrase = a.phrase.toLowerCase();
                    const bPhrase = b.phrase.toLowerCase();
                    const keyword = this.keyword.toLowerCase();

                    if (aPhrase === keyword) return -1;
                    if (bPhrase === keyword) return 1;
                    if (aPhrase.startsWith(keyword) && !bPhrase.startsWith(keyword)) return -1;
                    if (!aPhrase.startsWith(keyword) && bPhrase.startsWith(keyword)) return 1;
                    return aPhrase.length - bPhrase.length;
                });
            }

            const keywordExists = options.some(
                (search: RecentSearch) =>
                    typeof search.phrase === 'string' && search.phrase.toLowerCase() === this.keyword.toLowerCase(),
            );
            if (this.keyword && !keywordExists) {
                options.unshift({
                    phrase: this.keyword,
                    link: `${route('search')}?phrase=${this.keyword}`,
                    id: options.length,
                    label: this.keyword,
                    selectable: true,
                    temporary: true,
                });
            }

            return options;
        },
    },
    methods: {
        displayValue(option: RecentSearch) {
            return option?.temporary ? this.$t('search.searchBar.searchFor', { text: option.phrase }) : option.phrase;
        },
        handleKeydown() {
            this.onInput({ phrase: this.keyword });
        },
        focusInput() {
            // @ts-ignore cannot find the ref
            this.$refs.select.focusInput();
        },
        async clearRecentSearches() {
            const confirmed = await this.$modal.confirmDanger({
                title: this.$t('search.searchBar.clearRecentSearches'),
                text: this.$t('search.searchBar.clearSearchConfirmationText'),
                submitText: this.$t('yes'),
            });
            if (!confirmed) {
                return;
            }
            this.$store.dispatch('search/clear');
        },
        filterRecentSearches(keyword: string = '') {
            this.keyword = keyword.toLowerCase();
        },
        clearSearch(): void {
            this.keyword = '';
            // @ts-ignore cannot find the ref
            this.$refs.select.$refs.vselect.onEscape();
            this.$emit('clear');
        },
        submitSearch(phrase: string): void {
            if (!this.withNavigation) {
                return;
            }
            this.$store.dispatch('search/applyParams', {
                phrase,
                university_id: this.universityId ?? null,
            });
            // @ts-ignore cannot find the ref
            this.$refs.select.close();
        },
        onInput(option: RecentSearch): void {
            if (option?.phrase) {
                this.$emit('input', option.phrase);
                this.submitSearch(option.phrase);
            }
        },
    },
});
</script>

<style scoped lang="scss">
.selector {
    &.default {
        :deep() {
            .vs__dropdown-toggle {
                @apply transition-all border-2 p-2 border-transparent;
            }
            .vs__dropdown-menu {
                @apply transition-all -mt-[1px] py-0 border-blue-300 rounded-br-lg rounded-bl-lg shadow-none border-2;
            }
            .icon-search {
                @apply cursor-pointer bg-slate-400 mr-2 z-[10] #{!important};
            }
            .vs--open {
                .vs__dropdown-toggle {
                    @apply bg-blue-100 rounded-tl-lg rounded-tr-lg border-blue-300;
                }
                .icon-search {
                    @apply bg-black #{!important};
                }
            }
            input.vs__search {
                @apply text-black leading-7 text-lg placeholder:text-slate-400;
            }
            .v-select:not(.vs--open) .vs__selected + .icon-search,
            .hide-options ul[role='listbox'] {
                @apply hidden;
            }
            .vs__clear {
                @apply relative z-[9];
            }
        }
    }
}
</style>
